import { useCallback, useEffect, useRef, useMemo } from 'react';
import { useSSEConnection } from './useSSEConnection';
import { useWebSocketConnection } from './useWebSocketConnection';
import { useChatActions } from './useChatActions';


interface UseChatConnectionOptions {
  sseEndpoint: string;
  wsEndpoint: string;
}

/**
 * Combined hook that manages both SSE (for text) and WebSocket (for voice) connections
 * with lazy initialization - connections are created only when needed
 */
export const useChatConnection = ({
  sseEndpoint,
  wsEndpoint,
}: UseChatConnectionOptions) => {
  const isVoiceModeRef = useRef(false);
  const wsInitializedRef = useRef(false);
  const activeSSEConnectionRef = useRef<EventSource | null>(null);
  const { addMessage, updateMessageContent, completeMessageStream } = useChatActions();

  // Create SSE connection for a specific message response (like OpenAI API)
  const createSSEConnectionForMessage = useCallback((messageId?: string) => {
    // Close any existing connection
    if (activeSSEConnectionRef.current) {
      console.log('🔌 Closing previous SSE connection');
      activeSSEConnectionRef.current.close();
      activeSSEConnectionRef.current = null;
    }

    console.log('🔗 Creating new SSE connection for message response...');
    const eventSource = new EventSource(sseEndpoint);
    activeSSEConnectionRef.current = eventSource;

    eventSource.onopen = () => {
      console.log('✅ SSE connection established for message response');
    };

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        switch (data.type) {
          case 'message':
            // New message from server
            const message = {
              ...data.data,
              timestamp: new Date(data.data.timestamp),
            };
            addMessage(message);
            break;
            
          case 'stream_chunk':
            // Streaming content chunk
            updateMessageContent(data.data.messageId, data.data.chunk, data.data.isThinking);
            break;
            
          case 'stream_complete':
            // Stream completed - close connection
            console.log('✅ Stream completed, closing SSE connection');
            completeMessageStream(data.data.messageId);
            eventSource.close();
            activeSSEConnectionRef.current = null;
            break;
            
          case 'typing':
            // Handle typing indicator if needed
            break;
            
          default:
            console.log('📨 SSE message:', data.type);
        }
      } catch (error) {
        console.error('❌ Failed to parse SSE message:', error);
      }
    };

    eventSource.onerror = (error) => {
      console.error('❌ SSE connection error:', error);
      eventSource.close();
      activeSSEConnectionRef.current = null;
    };
  }, [sseEndpoint, addMessage, updateMessageContent, completeMessageStream]);

  // WebSocket connection for voice messages (lazy initialization - no auto-connect)
  const wsConnection = useWebSocketConnection({
    endpoint: wsEndpoint,
    autoConnect: false,
  });

  // Send text message with per-request SSE connection (like OpenAI API)
  const sendTextMessage = useCallback(async (content: string) => {
    try {
      console.log('📤 Sending message and creating SSE connection for response...');
      
      // Send the message via HTTP POST
      const response = await fetch(`${sseEndpoint.replace('/stream', '/send')}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content,
          sender: 'user',
        }),
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('📤 Message sent successfully, response will be streamed via SSE');
      
      // Create SSE connection for this specific message response
      createSSEConnectionForMessage(result.message?.id);
      
      return result;
    } catch (error) {
      console.error('❌ Failed to send text message:', error);
      
      // Add red error message to chat as assistant response
      const errorMessage = {
        id: `error_${Date.now()}`,
        content: `❌ **Connection Error**: ${error instanceof Error ? error.message : 'Failed to send message'}. Please check your connection and try again.`,
        sender: 'assistant',
        timestamp: new Date(),
        type: 'markdown' as const,
      };
      
      addMessage(errorMessage);
      throw error;
    }
  }, [sseEndpoint, addMessage]);

  // Start voice session (with lazy WebSocket connection initialization)
  const startVoiceSession = useCallback(() => {
    console.log('🎙️ Starting voice session...');
    isVoiceModeRef.current = true;
    
    try {
      // Initialize WebSocket connection on first voice interaction if not already done
      if (!wsInitializedRef.current) {
        console.log('🔗 Initializing WebSocket connection for first voice session...');
        wsConnection.connect();
        wsInitializedRef.current = true;
        
        // Wait for connection to establish before starting voice session
        const waitForConnection = () => {
          if (wsConnection.isConnected) {
            console.log('✅ WebSocket connected, starting voice session');
            wsConnection.startVoiceSession();
          } else if (wsConnection.isReconnecting) {
            // Still connecting, wait a bit more
            setTimeout(waitForConnection, 200);
          } else {
            console.warn('⚠️ WebSocket connection failed');
            // Add error message to chat
            const errorMessage = {
              id: `error_${Date.now()}`,
              content: `❌ **Voice Connection Error**: Unable to establish voice connection. Please check your connection and try again.`,
              sender: 'assistant',
              timestamp: new Date(),
              type: 'markdown' as const,
            };
            addMessage(errorMessage);
            isVoiceModeRef.current = false;
          }
        };
        
        // Start checking connection status
        setTimeout(waitForConnection, 100);
      } else if (wsConnection.isConnected) {
        // Connection already established, start session immediately
        console.log('✅ Using existing WebSocket connection');
        wsConnection.startVoiceSession();
      } else {
        // Connection exists but not ready, wait for it
        const waitForReconnection = () => {
          if (wsConnection.isConnected) {
            wsConnection.startVoiceSession();
          } else {
            setTimeout(waitForReconnection, 200);
          }
        };
        waitForReconnection();
      }
    } catch (error) {
      console.error('❌ Failed to start voice session:', error);
      
      // Add error message to chat
      const errorMessage = {
        id: `error_${Date.now()}`,
        content: `❌ **Voice Error**: ${error instanceof Error ? error.message : 'Failed to start voice session'}. Please try again.`,
        sender: 'assistant',
        timestamp: new Date(),
        type: 'markdown' as const,
      };
      
      addMessage(errorMessage);
      isVoiceModeRef.current = false;
    }
    
    return true;
  }, [wsConnection, addMessage]);

  // Send voice data
  const sendVoiceData = useCallback((audioData: ArrayBuffer | Blob) => {
    if (isVoiceModeRef.current) {
      return wsConnection.sendVoiceData(audioData);
    }
    return false;
  }, [wsConnection]);

  // End voice session
  const endVoiceSession = useCallback(() => {
    console.log('Ending voice session...');
    isVoiceModeRef.current = false;
    return wsConnection.endVoiceSession();
  }, [wsConnection]);

  // Handle voice recording start (toggle on - like Grok)
  const handleVoiceStart = useCallback(() => {
    console.log('🎙️ Voice session toggle ON');
    startVoiceSession();
  }, [startVoiceSession]);

  // Handle voice recording stop (toggle off - like Grok)
  const handleVoiceStop = useCallback(() => {
    console.log('🎙️ Voice session toggle OFF');
    endVoiceSession();
    
    // Close WebSocket connection when voice session ends
    if (wsInitializedRef.current && wsConnection.isConnected) {
      console.log('🔌 Closing WebSocket connection');
      wsConnection.disconnect();
      wsInitializedRef.current = false;
    }
  }, [endVoiceSession, wsConnection]);

  // Handle voice data from recording
  const handleVoiceData = useCallback((audioBlob: Blob) => {
    if (isVoiceModeRef.current && wsInitializedRef.current) {
      console.log('🎤 Sending voice data via WebSocket');
      sendVoiceData(audioBlob);
    } else {
      console.warn('⚠️ Voice data received but WebSocket not ready or not in voice mode');
    }
  }, [sendVoiceData]);

  // No automatic connection initialization
  // Connections will be created lazily when needed

  // Cleanup
  useEffect(() => {
    return () => {
      if (isVoiceModeRef.current) {
        endVoiceSession();
      }
    };
  }, [endVoiceSession]);

  // Cleanup function to close active SSE connection
  const cleanup = useCallback(() => {
    if (activeSSEConnectionRef.current) {
      console.log('🔌 Cleaning up active SSE connection');
      activeSSEConnectionRef.current.close();
      activeSSEConnectionRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  // Memoize the return object to prevent unnecessary re-renders
  return useMemo(() => ({
    // Text communication (per-message SSE)
    sendTextMessage,
    
    // Voice communication (WebSocket) - disabled for now
    handleVoiceStart,
    handleVoiceStop,
    handleVoiceData,
    
    // Connection status
    connectionStatus: 'disconnected', // No persistent connection
    isSSEActive: activeSSEConnectionRef.current !== null,
    
    // Cleanup
    cleanup,
  }), [
    sendTextMessage,
    handleVoiceStart,
    handleVoiceStop,
    handleVoiceData,
    cleanup,
  ]);
};